import logo from "@assets/logo.png";
import { Link } from "@tanstack/react-router";

export default function Landing() {
  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <div
        className="absolute -top-50 -right-25 w-[31.25rem] h-[31.25rem] rounded-full bg-[rgba(32,191,107,0.08)]"
        aria-hidden="true"
      />
      <div
        className="absolute top-150 -left-25 w-[21.875rem] h-[21.875rem] rounded-full bg-[rgba(156,77,255,0.07)]"
        aria-hidden="true"
      />
      <div
        className="absolute bottom-75 -right-15 w-[15.625rem] h-[15.625rem] rounded-full bg-[rgba(56,182,255,0.07)]"
        aria-hidden="true"
      />
      <div className="h-screen max-h-screen overflow-y-auto">
        <div className="px-5 pt-4 pb-9 flex flex-col gap-7 items-center">
          <div className="max-w-[68.75rem] w-full flex flex-col gap-6">
            <div className="flex flex-col md:flex-row gap-6 items-center justify-between pt-2">
              <div className="flex flex-col gap-3 flex-1 max-w-[35rem]">
                <span className="self-start bg-text text-white text-[0.625rem] font-bold tracking-widest uppercase rounded-full px-3 py-1">
                  Invite only
                </span>
                <h1 className="text-text text-[2.625rem] font-black leading-[3.125rem] tracking-tight">
                  Compete with <span className="text-coral">friends</span>,{" "}
                  <span className="text-purple">track steps</span>,{" "}
                  <span className="text-pink">win bragging rights</span>
                </h1>
                <p className="text-text-muted text-base leading-6 max-w-[37.5rem]">
                  Connect Google Health or Apple Health — then create or join a step competition.
                  Live leaderboards, team progress, and friendly challenges. FootSlapping is
                  invite-only, so it stays a place for people who actually show up.
                </p>
                <div className="flex flex-row flex-wrap gap-3 pt-4">
                  <Link
                    to="/request-access"
                    className="bg-aqua rounded-2xl py-3.5 px-6 items-center justify-center shadow-[1px_1px_0px_0px_#1c1b2e] border border-text no-underline inline-flex font-inherit cursor-pointer"
                  >
                    <span className="text-white font-extrabold text-sm">Request an invite</span>
                  </Link>
                  <Link
                    to="/sign-in"
                    className="bg-white rounded-2xl py-3.5 px-6 items-center justify-center border border-text no-underline inline-flex font-inherit cursor-pointer"
                  >
                    <span className="text-text font-bold text-sm">Sign in</span>
                  </Link>
                </div>
                <span className="text-text-dim text-xs font-medium pt-1">
                  Already have an invite code?{" "}
                  <Link to="/sign-up" className="text-aqua font-bold no-underline hover:underline">
                    Sign up now
                  </Link>
                </span>
              </div>
              <div className="items-center justify-center p-4 flex-1 flex justify-end">
                <div className="bg-white p-6 rounded-3xl shadow-[1px_1px_0px_0px_#1c1b2e] border border-text -rotate-2 relative">
                  <img
                    src={logo}
                    alt="FootSlapping logo"
                    className="w-[20rem] h-[11.25rem] object-contain"
                  />
                  <div className="absolute -bottom-2 -right-2 bg-lime border border-text rounded-lg px-3 py-1 rotate-[4deg] shadow-[1px_1px_0px_0px_#1c1b2e]">
                    <span className="text-white font-extrabold text-xs">SLAP THOSE FEET!</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-row flex-wrap gap-4 pt-4">
              {[
                {
                  emoji: "👟",
                  title: "Connect any provider",
                  desc: "Google Health or Apple Health — pick your favorite and your steps sync automatically.",
                },
                {
                  emoji: "🏆",
                  title: "Create competitions",
                  desc: "Set a date range, invite friends, and watch the leaderboard update in real time.",
                },
                {
                  emoji: "👥",
                  title: "Team up",
                  desc: "Create or join teams. Combine step totals and climb the ranks together.",
                },
                {
                  emoji: "📊",
                  title: "Live leaderboards",
                  desc: "See where you stand at a glance. Daily syncs keep scores fresh and motivation high.",
                },
              ].map((f) => (
                <div
                  key={f.title}
                  className="bg-card p-6 rounded-[20px] border border-[rgba(28,27,46,0.2)] shadow-[1px_1px_0px_0px_#1c1b2e] flex flex-col gap-2 w-[calc(50%-0.5rem)]"
                >
                  <span className="text-3xl" role="img" aria-label={f.title}>
                    {f.emoji}
                  </span>
                  <span className="text-text text-lg font-black">{f.title}</span>
                  <span className="text-text-muted text-sm leading-[1.375rem]">{f.desc}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-4 pt-8">
              <h2 className="text-text text-[1.75rem] font-black tracking-tight mb-2">
                How it works
              </h2>
              <div className="flex flex-col gap-0">
                {[
                  {
                    num: 1,
                    title: "Get your invite",
                    desc: "Request access or use a code from a friend already inside. No credit card, no subscription.",
                    bg: "bg-aqua",
                  },
                  {
                    num: 2,
                    title: "Connect a step provider",
                    desc: "Link Google Health or Apple Health with a single tap. Your data stays private.",
                    bg: "bg-purple",
                  },
                  {
                    num: 3,
                    title: "Start competing",
                    desc: "Create a competition or join one with an invite code. Invite friends and let the steps decide.",
                    bg: "bg-pink",
                  },
                ].map((step, i) => (
                  <div key={step.num}>
                    <div className="flex flex-row gap-4 items-start">
                      <div
                        className={`w-10 h-10 rounded-full ${step.bg} border border-text flex items-center justify-center shadow-[1px_1px_0px_0px_#1c1b2e]`}
                      >
                        <span className="text-white text-base font-extrabold">{step.num}</span>
                      </div>
                      <div className="flex-1 pt-2 flex flex-col gap-1.5">
                        <span className="text-text text-[1.0625rem] font-bold">{step.title}</span>
                        <span className="text-text-muted text-sm leading-[1.375rem]">
                          {step.desc}
                        </span>
                      </div>
                    </div>
                    {i < 2 && <div className="w-1 bg-text ml-[0.875rem] h-5" />}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col items-center bg-[#e8fbf6] rounded-3xl p-10 border border-text shadow-[1px_1px_0px_0px_#1c1b2e] gap-2.5 mt-8">
              <span className="text-5xl" role="img" aria-label="Invite">
                🔒
              </span>
              <span className="text-text text-[1.75rem] font-black">Free, but invite-only</span>
              <span className="text-text-muted text-base leading-6 text-center max-w-[31.25rem]">
                No premium tiers, no hidden costs — every feature is free. We just keep the door
                invite-only so it stays a small, real community instead of an open feed.
              </span>
              <Link
                to="/request-access"
                className="bg-lime rounded-2xl py-4 px-8 items-center shadow-[1px_1px_0px_0px_#1c1b2e] border border-text no-underline inline-flex mt-2 cursor-pointer font-inherit"
              >
                <span className="text-white font-extrabold text-sm">Request an invite</span>
              </Link>
              <span className="text-text-dim text-xs font-medium">
                Have a code already?{" "}
                <Link to="/sign-up" className="text-aqua font-bold no-underline hover:underline">
                  Sign up
                </Link>
              </span>
              <div className="flex flex-row items-center gap-3 mt-2">
                <a
                  href="https://apps.apple.com/app/id6781928157"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83"
                    alt="Download on the App Store"
                    className="h-[2.75rem] w-auto"
                  />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.footslapping"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                    alt="Get it on Google Play"
                    className="h-[2.75rem] w-auto"
                  />
                </a>
              </div>
            </div>

            <div className="flex flex-col items-center py-10 mt-8">
              <span className="text-text-dim text-sm font-medium text-center">
                FootSlapping — walk, repeat, compete, repeat, slap feet, slap feet.
              </span>
              <div className="flex flex-row gap-4 pt-3">
                <Link
                  to="/privacy"
                  className="text-text-muted text-xs font-medium no-underline hover:underline"
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/terms"
                  className="text-text-muted text-xs font-medium no-underline hover:underline"
                >
                  Terms of Service
                </Link>
                <Link
                  to="/faq"
                  className="text-text-muted text-xs font-medium no-underline hover:underline"
                >
                  How Syncing Works
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
